import { api } from '@/services/api'
import { resetState } from '@/store/helpers'
import i18n from '@/i18n'
import * as appConstants from '@/config/constants.js'
import _ from "lodash"
import moment from "moment"
import { CACHE } from '../config/constants'
// import { resolve } from 'core-js/fn/promise'

// this is for internal reference only.  When the user
// is updated, the base apiUrl is updated accordingly for use
// in api calls.  It's assumed that actions are being performed
// on the user who is logged in.
let apiUrl = null


const initState = () => {
  return {
    // general account info
    user: {},
    isLinguist: false,
    linguistProfile: {},
    photoUpdatedAt: new Date().getTime(),
    isSignedInOnMobile: false,
    devicesLoaded: false,
    devices: [],
    billingAccounts: [], // user could have access to more than one billing account
    activeBillingAccount: null,
    clientConfig: {},
    clientConfigReloadAt: null, // a timestamp
    upcomingCallsData: null,

    // organization memberships
    memberships: [],
    activeOrganization: null,

    // call history
    customerCallHistoryLoaded: false,
    customerCallHistory: [],
    linguistCallHistoryLoaded: false,
    linguistCallHistory: [],

    // transaction history
    sessionChargeHistoryLoaded: false,
    sessionChargeHistory: [],
    packageChargeHistoryLoaded: false,
    packageChargeHistory: [],

    // customer account balance & usage
    hasPaymentMethod: false,
    autoreloadMinutePackage: null,
    subscribedMinutePackage: null,
    hasUnlimitedUse: false,
    hasUnlimitedUseUntil: false,
    subscriptionPeriods: [],
    subscriptionPeriodsLoadedAt: null,
    currentSubscriptionPeriod: null,
    usagePeriods: [],
    currentUsagePeriod: null,

    // purchase options
    packagePromoCode: null,
    availablePackagesLoaded: false,
    packagesAvailable: false,
    availablePackages: [],

    // misc
    experimentalFeatureConfigLoaded: false,
    experimentalFeatureConfig: null,
    favoriteLinguists: [],
    referrerLoaded: false,
    referrer: null,
    referralTokenLoaded: false,
    referralToken: null,

    //zoomInfo
    zoomUserType: null, //account type of current zoom user 

    //participant records for current user
    linguistParticipantRecords: [],
    linguistParticipantRecordsLoaded: false,

    organizationID: null,
    showPreCallCustomFieldsDialog: false,
    showPostCallCustomFieldsDialog: false,
    featureConfig: false,
    customFieldsDefaultValues: {}
  }
}

const state = initState()
const fieldType = {
  input: "input",
  dropdown: "dropdown"
}
// TODO: add getters for "public" vs "custom" packages
const getters = {
  getMyState: state => state.user,
  displayName: function (state) {
    if (state.user.preferredName != null) {
      return state.user.preferredName
    }

    if (!!state.user.firstName && !!state.user.lastName) {
      return `${state.user.firstName} ${state.user.lastName}`
    }

    return state.user.firstName
  },
  avatarUrl: function (state) {
    if (state.user.avatarURL) {
      return state.user.avatarURL + "?" + state.photoUpdatedAt
    }
    // default gravatar placeholder
    return "https://www.gravatar.com/avatar/?d=mp&s=250"
  },
  isBlockedUser: function (state) {
    return (!!state.user.featureFlags && state.user.featureFlags.indexOf(appConstants.featureFlagIsBlockedUser) != -1) ? true : false
  },
  isOrgOwner: function (state) {
    return (!!state.user.featureFlags && state.user.featureFlags.includes("users:type:org-owner")) ? true : false
  },
  isLinguistTwo: function (state) {
    //WARNING! HACK!!
    if ( state.user.featureFlags ) {
      if (
        (state.user.featureFlags.indexOf(appConstants.featureFlagActiveLinguist) != -1)
        || (state.user.featureFlags.indexOf(appConstants.featureFlagProspectiveLinguist) != -1)
        || (state.user.featureFlags.indexOf(appConstants.featureFlagTestCallLinguist) != -1)
      ) {
        return true
      }
      return false
    }
  },
  getCustomFields: (state) => (session, isPre) => {
    /*t this functions is for mainly getting already set pre and post config fields and
        setting their default values either from user object or from session object
    */
    const configType = isPre ? "preFeatureConfig" : "postFeatureConfig";
    if (state.activeBillingAccount?.[configType]) {
      for (const prop in state.activeBillingAccount[configType]) {
        let metaData = ''
        if (isPre) {
          // First check if the precedence API has returned any default custom fields values,
          // then the values from the session object, lastly from the user object
          metaData = state.customFieldsDefaultValues?.sessionMetadata || session?.metadata || state.user?.sessionMetadata;
        } else {
          metaData = session?.metadata 
        }
        const property = state.activeBillingAccount[configType][prop];
        if (metaData) {
          const matchingMeta = Object.entries(metaData).find(
            ([key]) => property.key === key
          );
          if (matchingMeta) property.value = matchingMeta[1];
          else property.value = "";
          state.activeBillingAccount[configType][prop] = property;
        } else property.value = "";
      }
      
      return state.activeBillingAccount[configType];
    }
    return {}
  },
}

const mutations = {
  reset(state) {
    resetState(state, initState())
  },
  setUser(state, user) {

    // set user/linguist state
    state.linguistProfile = {}
    state.user = user
    state.isLinguist = !!user.linguistProfile
    if (state.isLinguist) {
      state.linguistProfile = user.linguistProfile
    }

    apiUrl = "/v1/users/" + user.id
  },
  setBillingAccounts(state, accounts = []) {
    if (!accounts || accounts.length === 0) {
      state.activeBillingAccount = null
      state.billingAccounts = []
      return
    }

    state.billingAccounts = accounts
  },
  updateFeatureConfigFlag(state, payload) {
    state.featureConfig = payload
  },
  updatePreCallCustomFieldsFlag(state, payload) {
    state.showPreCallCustomFieldsDialog = payload
  },
  updatePostCallCustomFieldsFlag(state, payload) {
    state.showPostCallCustomFieldsDialog = payload
  },
  setActiveBillingAccount(state, account = null) {
    if (account === null) {
      state.sessionChargeHistoryLoaded = false
      state.sessionChargeHistory = []
      state.packageChargeHistoryLoaded = false
      state.packageChargeHistory = []
      state.autoreloadMinutePackage = null
      state.subscribedMinutePackage = null
      state.hasUnlimitedUse = false
      state.hasUnlimitedUseUntil = false
      state.subscriptionPeriods = []
      state.subscriptionPeriodsLoadedAt = null
      state.hasUnlimitedUse = false
      state.hasUnlimitedUseUntil = false
      state.hasPaymentMethod = false
      state.currentSubscriptionPeriod = null
      state.usagePeriods = []
      state.currentUsagePeriod = null
      state.clientConfig = {}

      return
    }
    state.activeBillingAccount = account
    state.organizationID = account.organizationID
    // check for any payment methods
    if (!!account.paymentMethods && account.paymentMethods.length > 0) {
      state.hasPaymentMethod = true
    } else {
      state.hasPaymentMethod = false
    }

    // check currently active subscription periods
    if (!!account.subscriptionPeriods && account.subscriptionPeriods.length > 0) {
      state.subscriptionPeriods = account.subscriptionPeriods
      state.subscriptionPeriodsLoadedAt = new Date().getTime()
      const unlimitedUseUntil = hasUnlimitedUseUntil(account.subscriptionPeriods)
      if (unlimitedUseUntil !== false) {
        state.hasUnlimitedUse = true
        state.hasUnlimitedUseUntil = unlimitedUseUntil
      }
      const cp = getCurrentPeriod(account.subscriptionPeriods)
      state.currentSubscriptionPeriod = (cp) ? cp : null
    } else {
      state.currentSubscriptionPeriod = null
      state.subscriptionPeriods = []
    }

    // check for usage periods
    if (!!account.usagePeriods && account.usagePeriods.length > 0) {
      state.usagePeriods = account.usagePeriods || []
      const cp = getCurrentPeriod(state.usagePeriods)
      state.currentUsagePeriod = (cp) ? cp : null
    } else {
      state.usagePeriods = []
      state.currentUsagePeriod = null
    }

    // check minute packages
    state.autoreloadMinutePackage = account.autoreloadPackage || null
    state.subscribedMinutePackage = account.subscribedPackage || null
  },
  setClientConfig(state, conf = {}) {
    let reloadAt = null
    const now = new Date().getTime()
    if (conf.reloadAt) {
      reloadAt = new Date(conf.reloadAt).getTime()
    }
    if (!reloadAt || reloadAt < now) {
      reloadAt = now + CACHE.CLIENT_CONFIG
    }

    state.clientConfig = conf
    state.clientConfigReloadAt = reloadAt
  },
  setOrganizationMemberships(state, memberships = []) {
    if (!memberships || memberships.length === 0) {
      state.memberships = []
      state.activeMembership = null
      return
    }

    state.memberships = memberships
    if (memberships.length > 0) {
      state.activeMembership = memberships[0]
    }
  },
  setZoomUserType(state, userStatus){
    state.zoomUserType = userStatus.type
  },
  setLinguistParticipantRecords(state, records){
    state.linguistParticipantRecords = records
    state.linguistParticipantRecordsLoaded = true
  },
  setCustomFieldsDefaultValues(state, ccfDefaultValues) {
    state.customFieldsDefaultValues = ccfDefaultValues;
  }
}

const actions = {
  refresh({ commit, dispatch }, userId = null) {
    if (userId) {
      apiUrl = "/v1/users/" + userId
    }
    return new Promise((resolve, reject) => {
      var userRes = null
      api.get(apiUrl)
        .then((res) => {
          commit('setUser', res.data)
          userRes = res.data
        })
        .then(() => {
          return Promise.all([      
            dispatch('loadBillingAccount'),
            dispatch('loadBillingAccounts'),      
            dispatch('loadBillingAccountConfig', {force: true}),
            dispatch('loadUpcomingCalls'),
            dispatch('loadCallHistory'),
           
          ])
        })
        .then(() => {
          resolve(userRes)
        })
        .catch(reject)
    })
  },
  loadBillingAccounts({  dispatch }) {
    return new Promise((resolve, reject) => {
      api.get(apiUrl+'/billing-accounts').then((res) => {
        dispatch('setBillingAccounts', res.data)
        resolve(res.data)
      }).catch(reject)
    })
  },
  loadBillingAccount({commit, state}) {
    if (!state.user.billingAccountID) {
      return Promise.reject(new Error('no billing account id for user'))
    }

    return new Promise((resolve, reject) => {
      api.get(`/v1/billing-accounts/${state.user.billingAccountID}`)
        .then((res) => {
          let properties = []
          let preproperties = []
          let postproperties = []
          let jsonSchema = res?.data?.featureConfig?.sessionMetadata?.fieldsJsonSchema
          if(jsonSchema) {
            let fields = jsonSchema?.properties ? _.orderBy(jsonSchema?.properties, ['order'], ['asc']) : null
            if(fields){
              for (let key of Object.keys(fields)) {
                let fieldValue = fields[key]
                if(!fieldValue.deprecated) {
                  if(fieldValue.oneOf) {
                    fieldValue.inputType = fieldType.dropdown
                    fieldValue.placeholder = i18n.global.t('metaData.dropdownPlaceholder')
  
                    // Update oneOf values
                    fieldValue.oneOf.map( x => {
                      x.text = x.title
                      x.value = x.const
                      delete x.title
                      delete x.const
                    })
                  }
                  else {
                    fieldValue.inputType = fieldType.input
                    fieldValue.placeholder = i18n.global.t('metaData.textFieldPlaceholder')
                  }
                  
                  // Check if key is in requiredFields
                  let requiredFields = jsonSchema?.required
                  if (requiredFields?.includes(key)) {
                    fieldValue.required = true
                  }
  
                  // Assign metaValue to fieldValue.value
                  let metaData = state?.user?.sessionMetadata ? Object.entries(state?.user?.sessionMetadata) : null
                  if(metaData){
                    for (let [metaKey, metaValue] of metaData){
                      if(metaKey === key) {
                        fieldValue.value = metaValue;
                      }
                    }
                  }
                  fieldValue.key = key;
                  properties.push(fieldValue)
                  //for pre and post ccf
                  let tempFieldValue = { ...fieldValue }; //deep copy
                  if (tempFieldValue?.contexts?.length) {
                    tempFieldValue.contexts.forEach((element) => {
                      tempFieldValue.required = element.required;
                      if (element.key == "precall") {
                        preproperties.push({ ...tempFieldValue });
                      } else if (element.key == "postcall") {
                        postproperties.push({ ...tempFieldValue });
                      }
                    });
                  }
                }
              }
            }
            // Update featureConfig with properties
            res.data.featureConfig = properties
            commit('updateFeatureConfigFlag', true)

            if (preproperties.length && state.user.billingAccount?.featureConfig?.orgMemberManagement?.customFieldsManagement?.enabled) {
              res.data.preFeatureConfig = preproperties;
              commit("updatePreCallCustomFieldsFlag", true);
            }
            if (postproperties.length && state.user.billingAccount?.featureConfig?.orgMemberManagement?.customFieldsManagement?.enabled) {
              res.data.postFeatureConfig = postproperties;
              commit("updatePostCallCustomFieldsFlag", true);
            }
          }
          else {
            commit('updateFeatureConfigFlag', false)
          }
          commit('setActiveBillingAccount', res.data)
          resolve(res.data)
        }).catch(reject)
    })
  },
  fetchCustomFieldDefaultValues({commit, state}, scenarioId) {
    const userId = state.user?.id || '';
    let url = `/v1/users/${userId}/customfields/defaults`
    if (scenarioId) {
      url += `?scenario_id=${scenarioId}`
    }
    return new Promise((resolve, reject) => {
      return api.get(url).then((res) => {
        commit('setCustomFieldsDefaultValues', res.data)
        resolve(res.data)
      }).catch(reject)
    })
  },
  setBillingAccounts({commit}, accounts = []) {
    if (!!accounts && accounts.length == 0) {
      commit('setBillingAccounts', [])
      // commit('setActiveBillingAccount', null)
    } else {
      commit('setBillingAccounts', accounts)
      // commit('setActiveBillingAccount', state.activeBillingAccount)
    }
  },
  loadBillingAccountConfig({commit, state}, data = {}) {
    if (!state.user.billingAccountID) {
      return Promise.reject(new Error('no billing account id for user'))
    }

    if (!!state.clientConfigReloadAt && new Date().getTime() < state.clientConfigReloadAt && !data.force) {
      return Promise.resolve(state.clientConfig)
    }

    return new Promise((resolve, reject) => {
      api.get(`/v1/billing-accounts/${state.user.billingAccountID}/client-config`)
        .then((res) => {
          commit('setClientConfig', res.data)
          resolve(res.data)
        })
        .catch(reject)
    })
  },
  loadUpcomingCalls({ state}, data ={}) {
    const now = moment().subtract(3, 'hours').utc();
    const role = state.isLinguist ? "linguist" : "creator";
    let params = [
      `type=scheduled_virtual`,
      `scheduledAfter=${now.format('YYYY-MM-DD')}`,
      `isEnded=false`,
      `role=${role}`,
      // `sort=createdAt:desc`,
    ]
    if (data.urlFilters) params = params.concat(data.urlFilters);
    const url = `/v1/sessions?${params.join('&')}`
    let sessmap = {}

    return new Promise((resolve, reject) => {
      return api.get(url).then((res) => {
          if (res && res.data && res.data.sessions && res.data.sessions.length > 0) {
            res.data.sessions.forEach((sess) => {
              sessmap[sess.id] = sess
            })
          }
          state.upcomingCallsData = Object.values(sessmap)
          state.upcomingCallsData.sort((a, b) => {
            const ta = moment(a.scheduledBeginAt)
            const tb = moment(b.scheduledBeginAt)
            if (ta.unix() < tb.unix()) {
              return -1
            } else if (ta.unix() > tb.unix()) {
              return  1
            }
            return 0
          })
        resolve(res.data)
      }).catch(reject)
    })
  },
  loadOrganizationMemberships({ commit }) {
    return new Promise((resolve, reject) => {
      api.get(apiUrl+'/memberships').then((res) => {
        commit('setOrganizationMemberships', res.data)
        resolve(res.data)
      }).catch(reject)
    })
  },
  //for getting the account type of zoom user
  getZoomUserType({ commit }){
    return new Promise((resolve, reject) => {
      api.get('/v1/integrations/zoom/user-type').then((res)=>{
        commit('setZoomUserType', res.data)
        resolve(res.data)
      })
      .catch(e=>{reject(e)})
    })
  },
  getLinguistParticipantData({ commit }){
    //for fetching linguist participant records
    return new Promise((resolve, reject)=>{
      api.get(apiUrl + '/session/participants?_skip=0&_limit=10&_relations=session&servicePlatform=zoom&connectionStatus=connecting,connected,disconnected&session_ended=false')
        .then((res)=>{
          commit('setLinguistParticipantRecords', res.data.participants)
          resolve(res.data)
        })
        .catch(reject)
    })
  },
  getLinguistParticipantDataForEndedSessions({ commit }){
    //for fetching linguist participant records for ended sessions
    return new Promise((resolve, reject)=>{
      api.get(apiUrl + '/session/participants?_skip=0&_limit=10&_relations=session&servicePlatform=zoom&connectionStatus=connecting,connected,disconnected&session_ended=true')
        .then((res)=>{
          commit('setLinguistParticipantRecords', res.data.participants)
          resolve(res.data)
        })
        .catch(reject)
    })
  },
  loadCallHistory({dispatch}) {
    let currentDate = new Date();
      currentDate = moment(currentDate).format("YYYY-MM-DDTHH:mm:ss");
    let params = [
      `_relations=rating`,
      `_criteria=billable,unresolved`,
      `happensBefore=${currentDate}`,
      `limit=${50}`,
      `role=creator`,
      `skip=${0}`,
      `servicePlatform=zoom`
    ]

    const url = `/v1/sessions?${params.join('&')}`
    return new Promise((resolve, reject) => {
      return api.get(url).then((res) => {
          if (res.data?.sessions?.length) {
            //load recently used secondary languages
            dispatch("loadRecentlyUsedLanguages", res.data.sessions);
          }
          resolve(res.data)
      }).catch(reject)
    })
  },
  loadRecentlyUsedLanguages({dispatch}, data) {
    let arrayUniqueByKey = [
      ...new Map(
        data.reverse().map((item) => [item["secondaryLangCode"], item])
      ).values(),
    ];
    arrayUniqueByKey.forEach((item) => {
      const payload = {
        secondaryLangCode: item.secondaryLangCode,
        timestamp: item.happensAt,
      };
      dispatch("updateListOfRecentlyUsedSecondaryLanguages", payload, { root: true });
    });
  },
  getUserInCallStatus({ rootState, dispatch }, meetingId) {
    let activeSession = null;
    return new Promise((resolve, reject) => {
      api
        .get(apiUrl + "/session-status")
        .then((res) => {
          if (!!res?.data?.sessions && res.data.sessions.length) {
            activeSession = getActiveSession(
              res.data.sessions,
              rootState.auth.deviceId,
              meetingId
            );
            if (activeSession?.sessionID) {
              dispatch("session/setSessionID", activeSession?.sessionID, {
                root: true,
              });
            }
          }
          resolve(activeSession);
        })
        .catch(reject);
    });
  },
  loadSchdeuledCallHistory({ dispatch }, meetingInfo) {
    let currentDate = new Date();
      currentDate = moment(currentDate).format("YYYY-MM-DDTHH:mm:ss");
    let params = [
      `_relations=rating`,
      `_criteria=billable,unresolved`,
      `happensBefore=${currentDate}`,
      `limit=${50}`,
      `role=creator`,
      `skip=${0}`,
      `servicePlatform=zoom`,
      `type=scheduled_virtual`
    ]
    let activeSession = null;
    const url = `/v1/sessions?${params.join('&')}`
    return new Promise((resolve, reject) => {
      return api.get(url)
      .then((res) => {
        if (res?.data?.results && res.data.results.length) {
          activeSession = getActiveScheduledSession(
            res.data.results,
            meetingInfo.meetingID
          );
          if (activeSession?.sessionID) {
            dispatch("session/setSessionID", activeSession?.sessionID, {
              root: true,
            });
          }
        }
        resolve(activeSession);
      })
      .catch(reject)
    })
  },
  // eslint-disable-next-line
  submitFeedback( {},payload) {
    return new Promise((resolve, reject) => {
      api.post(apiUrl + "/feedback", payload)
      .then(resolve)
      .catch(reject)
    })
  },
  
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}


function hasUnlimitedUseUntil(periods) {
  periods = periods || [];
  if (periods.length == 0) {
    return false;
  }

  const now = moment();
  for (var p of periods) {
    const begin = moment(p.beginAt);
    const end = moment(p.endAt);
    if (p.unlimitedUse) {
      if (begin.isBefore(now) && end.isAfter(now)) {
        return end;
      }
    }
  }

  return false;
}

function getCurrentPeriod (periods) {
  periods = periods || []
  if (periods.length == 0) {
    return false
  }
  const now = moment();
  for (var p of periods) {
    const begin = moment(p.beginAt)
    const end = moment(p.endAt)
    if (begin.isBefore(now) && end.isAfter(now)) {
      return p
    }
  }
  return false
}
function getActiveSession(sessions, deviceID, meetingId) {
  if (!!sessions && sessions.length) {
    //first sort the sessions latest first
    let activeSessions = sessions.sort((a, b) => {
      const ta = moment(a.session?.happensAt);
      const tb = moment(b.session?.happensAt);
      if (ta.unix() < tb.unix()) {
        return 1;
      } else if (ta.unix() > tb.unix()) {
        return -1;
      }
      return 0;
    });
    //then get the active session from it if any
    let activeSession = activeSessions.find((s) => {
      return (
        // s.deviceID === deviceID &&
        s.session?.servicePlatform == "zoom" &&
        s.session?.type == "immediate_virtual" &&
        s.session?.servicePlatformSessionID == meetingId
      );
    });
    return activeSession;
  }
  return {};
}

function getActiveScheduledSession(sessions, meetingID) {
  if (!!sessions && sessions.length) {
    //first sort the sessions latest first
    let activeSessions = sessions.sort((a, b) => {
      const ta = moment(a.session?.happensAt);
      const tb = moment(b.session?.happensAt);
      if (ta.unix() < tb.unix()) {
        return 1;
      } else if (ta.unix() > tb.unix()) {
        return -1;
      }
      return 0;
    });
    //then get the active session from it if any
    let activeSession = activeSessions.find((s) => {
      let currentDate = new Date();
      currentDate = moment(currentDate).format("YYYY-MM-DDTHH:mm:ss");
      return (
        currentDate <=  moment(s.scheduledEndAt).format("YYYY-MM-DDTHH:mm:ss")  &&
        s.servicePlatform == "zoom" &&
        s.type == "scheduled_virtual" &&
        s.servicePlatformMeta.meetingID == meetingID
      );
    });
    return activeSession;
  }
  return {};
}
